import Hyperlink from '@ingka/hyperlink';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Carousel from '@ingka/carousel';
import { config } from '../../config';
import { getCredentialUrl } from '../../utils';
import { CSSEvent } from '../../types/model';
import Event from '../../components/Event';
import ConditionalRender from '../../components/ConditionalRender';
import { useEffect, useState } from 'react';
import { getAllEventsFromCountry } from '../../services/storeEventApi';
import { formatEvents } from '../../contextProviders/AppContext/utils';
import NoEvents from '../../components/NoEvents';
import { useQuery } from 'react-query';
import { setEvents, useAppContext } from '../../contextProviders/AppContext';
import * as selectors from '../../contextProviders/AppContext/selectors';

const SubHeader = styled.h2`
  color: var(--colour-text-dark);
  margin-bottom: var(--space-75);

  ${({ theme }) => `${theme.breakpoints.lg} {
    margin-bottom: var(--space-100);
  }`}
`;

const Description = styled.p`
  white-space: pre-line;
  &:not(:last-of-type) {
    margin-bottom: var(--space-200);
    ${({ theme }) => `${theme.breakpoints.lg} {
      max-width: 550px;
      margin-bottom: var(--space-300);
    }`}
  }
`;

const RegisterHeader = styled.h3`
  margin-bottom: var(--space-75);

  ${({ theme }) => `${theme.breakpoints.lg} {
    margin-bottom: var(--space-100);
  }`}
`;

const CarouselWrapper = styled.div`
  margin: var(--space-400) auto;
  .carousel__button {
    z-index: 3;
  }
  .item {
    width: 85%;
    ${({ theme }) => `${theme.breakpoints.md} {
      width: unset;
    }`}
  }
`;

const NoStoreSelected = ({
  showStayInformedText = true,
  renderNoEvents = true,
}) => {
  const { t } = useTranslation();
  const { dispatch, useSelector } = useAppContext();
  const eventList = useSelector(selectors.getEvents);
  const [upcomingEvents, setUpcomingEvents] = useState<Array<CSSEvent>>([]);

  const fetchUpcomingEvents = async () => {
    try {
      return await getAllEventsFromCountry();
    } catch (e: any) {
      throw Error(e.code);
    }
  };

  const { data } = useQuery(['fetchUpcomingEvents'], fetchUpcomingEvents, {
    enabled: true,
    staleTime: 20 * 60 * 1000, // 20 minutes caching
  });

  useEffect(() => {
    if (data && data.length > 0) {
      setUpcomingEvents(
        formatEvents(data, {
          language: config.language,
          defaultLanguage: config.defaultLanguage,
        })
      );
      // this is to reset the app context when returning from an carousel Event
      try {
        if (
          JSON.stringify(upcomingEvents[0]) !== JSON.stringify(eventList[0])
        ) {
          dispatch(setEvents(data));
        }
      } catch (e) {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const carouselItems = upcomingEvents.map((event, i) => (
    <Event event={event} key={i} slideShow={true} />
  ));

  return (
    <>
      <ConditionalRender
        predicate={upcomingEvents.length > 0}
        fallback={renderNoEvents && <NoEvents showCarousel={false} />}
      >
        <SubHeader>{t('event.joinOurEvents')}</SubHeader>
        <Description>{t('event.noEventsDesc')}</Description>

        <CarouselWrapper>
          <Carousel
            id="events-carousel"
            children={carouselItems}
            isSlideShow={false}
            slideIndex={0}
            itemClass="item"
          />
        </CarouselWrapper>

        <div>
          <ConditionalRender predicate={showStayInformedText}>
            <RegisterHeader>{t('event.stayUptoDate')}</RegisterHeader>
            <Trans
              i18nKey="event.joinIKEAFamily"
              components={{
                JoinLink: (
                  <Hyperlink
                    url={getCredentialUrl(config.joinIkeaFamily)}
                    text={t('event.joinIKEAFamilyLink')}
                  />
                ),
                JoinLinkBusiness: (
                  <Hyperlink
                    url={getCredentialUrl(config.joinIkeaBusinessNetwork)}
                    text={t('event.ikeaBusinessNetwork')}
                  />
                ),
              }}
            />
          </ConditionalRender>
        </div>
      </ConditionalRender>
    </>
  );
};

export default NoStoreSelected;
